import React from "react";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import Table from "../../ui-components/table/table";
import Button, { ButtonThemes } from "../../ui-components/button/button";
import { LinehaulTrip } from "../../app/data/linehaul-mileage/models";
import dateFormats from "../../app/data/common/dateFormats";
import driverGray from "../../images/driver_gray.svg";
import driverBlue from "../../images/driver_blue.svg";
import editBlue from "../../images/edit_blue.svg";
import plusBlue from "../../images/plus__blue.svg";
import infoIcon from "../../images/info_blue.svg";
import { getAccessorialColumns } from "./accessorialColumns";
import { getLaneColumns } from "./getLaneColumns";
import { deleteTripException, linehaulMileageSelector } from "../../slices/linehaul-mileage/linehaulMileageSlice";

export interface TripOperationsTabProps {
  trip?: LinehaulTrip;
  onShowDriverModal: () => void;
  onAssignLane: () => void;
  onAddAccessorial: () => void;
  onReviewTrip: () => void;
  onUnReviewTrip: () => void;
}

const getInfoMessage = (driver: boolean, lane: boolean) => {
  if (!driver && !lane) {
    return "Please assign driver and lane to mark the trip as reviewed.";
  } else if (!lane) {
    return "Please assign lane to mark the trip as reviewed.";
  } else if (!driver) {
    return "Please assign driver to mark the trip reviewed.";
  }
};

export const TripOperationsTab: React.FC<TripOperationsTabProps> = (props) => {
  const linehaulMileageState = useSelector(linehaulMileageSelector);
  const dispatch = useDispatch();
  const assignedLane = props.trip?.lane;

  return (
    <>
      {(!props.trip?.driver || !props.trip?.lane) && (
        <div className="xgs-lmi__info">
          <img src={infoIcon} alt="info" />
          {getInfoMessage(!!props.trip?.driver, !!props.trip?.lane)}
        </div>
      )}
      <div className="xgs-lmi__details__block">
        <div className="xgs-lmi__details__block-title">Route Summary</div>
        <div className="xgs-lmi__details__header">
          <div className="xgs-lmi__details__header__item">
            <label>Manifest:</label>
            <div>{props.trip?.manifestNumber}</div>
          </div>
          <div className="xgs-lmi__details__header__item">
            <label>Dispatched at:</label>
            <div>{moment(props.trip?.dispatchedDate, "YYYYMMDD").format(dateFormats.uiDate)}</div>
          </div>
          <div className="xgs-lmi__details__header__item">
            <label>Origin:</label>
            <div>{props.trip?.originTerminalName} <span>{props.trip?.originTerminalNumber}</span></div>
          </div>
          <div className="xgs-lmi__details__header__item">
            <label>Destination:</label>
            <div>{props.trip?.destinationTerminalName} <span>{props.trip?.destinationTerminalNumber}</span></div>
          </div>
        </div>
      </div>
      <div className="xgs-lmi__details__block">
        <div className="xgs-lmi__details__block-title">Driver Summary</div>
        <div className="xgs-lmi__details__driver">
          {!props.trip?.driver ? (
            <>
              <img src={driverGray} alt="Driver" />
              <div className="xgs-lmi__details__driver__info">
                <label>No driver assigned</label>
              </div>
              <Button className="xgs-lmi__details__driver__info__button--large" onClick={props.onShowDriverModal} theme={ButtonThemes.blue}>Assign a driver</Button>
              <Button className="xgs-lmi__details__driver__info__button--small" onClick={props.onShowDriverModal} theme={ButtonThemes.blue}>Assign</Button>
            </>
          ) : (
            <>
              <img src={driverBlue} alt="Driver" />
              <div className="xgs-lmi__details__driver__info">
                <label>Assigned driver</label>
                <div className="xgs-lmi__details__driver__info__name">{props.trip?.driver?.firstName + " " + props.trip?.driver?.lastName} <span>{props.trip?.driver?.driverId}</span></div>
              </div>
              <Button onClick={props.onShowDriverModal} className="xgs-lmi__details__driver__edit" theme={ButtonThemes.transparent}><img src={editBlue} alt="Edit" />&nbsp;&nbsp; Edit</Button>
            </>
          )}
        </div>
      </div>
      <div className={`xgs-lmi__details__block ${!assignedLane ? "hide-table-content" : ""}`}>
        <div className="xgs-lmi__details__block-title">Assigned Lane</div>
        <Table
          columns={getLaneColumns(props.trip?.lane?.laneId, true)}
          data={assignedLane ? [assignedLane] : []}
        />
        <Button onClick={props.onAssignLane} className="xgs-lmi__details__table-button" theme={ButtonThemes.blue}>
          <img src={plusBlue} alt="Plus" />{!props.trip?.lane ? "Assign Lane" : "Reassign lane"}
        </Button>
      </div>
      <div className={`xgs-lmi__details__block ${props.trip?.exceptions.length ? "" : "hide-table-content"}`}>
        <div className="xgs-lmi__details__block-title">Exceptions</div>
        {!!props.trip && (
          <Table
            columns={getAccessorialColumns((tripId: string, exceptionId: string) => {
              !linehaulMileageState.requestStarted && dispatch(deleteTripException(tripId, exceptionId));
            }, !!linehaulMileageState.requestStarted)}
            data={props.trip?.exceptions}
          />
        )}
        <Button onClick={props.onAddAccessorial} className="xgs-lmi__details__table-button" theme={ButtonThemes.blue}>
          <img src={plusBlue} alt="Plus" />Add new
        </Button>
      </div>
      {!props.trip?.isReviewed && (
        <Button style={{ marginBottom: 140 }} spinner={linehaulMileageState.requestStarted} disabled={!props.trip?.lane || !props.trip?.driver} onClick={props.onReviewTrip} theme={ButtonThemes.blue}>
          Review
        </Button>
      )}
      {props.trip?.isReviewed && (
        <Button style={{ marginBottom: 140 }} spinner={linehaulMileageState.requestStarted} disabled={!props.trip?.lane || !props.trip?.driver} onClick={props.onUnReviewTrip} theme={ButtonThemes.gray}>
          Unreviewed
        </Button>
      )}
    </>
  );
};
